import { CalendarDays, Clock, MapPin } from 'lucide-react';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Error, Spinner } from 'src/components';
import { useGetConferenceFromFirestore, useQueryHelper } from 'src/queries';
import { useLanguageState, useOrganizationState } from 'src/state';
import { Agenda, Prelegent } from 'src/types';

import { CodeModal } from './components/CodeModal';
import {
  AgendaDateContainer,
  AgendaDuraiton,
  AgendaItem,
  AgendaTextContainer,
  AgendaTime,
  AgendaTimeText,
  Banner,
  Description,
  Label,
  LabelsContainer,
  LabelText,
  OrgContainer,
  OrgDescription,
  OrgLogo,
  OrgText,
  OrgTextWrapper,
  PrelegentContainer,
  PrelegentImage,
  PrelegentName,
  PrelegentRole,
  PrelegentsContainer,
  PrelegentTextContainer,
  ScrollContainer,
  Title,
} from './Conference.styled';

export const Conference: FC = () => {
  const { id } = useParams();
  const { organizationData } = useOrganizationState();
  const { language: currentLang } = useLanguageState();
  const ConferenceDocument = useGetConferenceFromFirestore(organizationData.hotel || '', id || '');
  const { data: conferenceData, refetch } = ConferenceDocument;
  const { isLoading, isError } = useQueryHelper({ queries: [ConferenceDocument] });

  const getMinutesDifference = (timeFrom: string, timeTo: string) => {
    const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
    const [toHours, toMinutes] = timeTo.split(':').map(Number);

    const fromTotalMinutes = fromHours * 60 + fromMinutes;
    const toTotalMinutes = toHours * 60 + toMinutes;

    return toTotalMinutes - fromTotalMinutes;
  };

  const getPrelegents = (ids: string[]) => {
    if (conferenceData && conferenceData.prelegents) {
      const uniqueIds = [...new Set(ids)];
      const prelegents = conferenceData.prelegents.filter((c: Prelegent) => uniqueIds.includes(c.id));
      return prelegents;
    }
    return null;
  };

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (<Error errorMessage="Błąd pobierania danych" action={refetch} />
    );
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Szczegóły konferencji</title>
        <meta name="description" content="Hotelspot - szczegóły konferencji" />
      </Helmet>
      <div>
        <CodeModal conferenceCode={conferenceData?.code} />
        <ScrollContainer>
          <Title>
            {conferenceData?.title[currentLang]}
          </Title>
          <Banner src={conferenceData?.cover_photo || ''} />
          <LabelsContainer>
            <Label>
              <CalendarDays size={18} color="#1C1C1C" />
              <LabelText>{conferenceData?.date}</LabelText>
            </Label>
            <Label>
              <Clock size={14} color="#1C1C1C" />
              <LabelText>{conferenceData?.time_from} - {conferenceData?.time_to}</LabelText>
            </Label>
            <Label>
              <MapPin size={18} color="#1C1C1C" />
              <LabelText>{conferenceData?.place}</LabelText>
            </Label>
          </LabelsContainer>
          <Description>
            {conferenceData?.description[currentLang]}
          </Description>
          <OrgContainer>
            <OrgLogo src={conferenceData?.logo || ''} />
            <OrgTextWrapper>
              <OrgText>{conferenceData?.organizator_name}</OrgText>
              <OrgDescription>Organizator konferencji</OrgDescription>
            </OrgTextWrapper>
          </OrgContainer>
          <Title>Agenda</Title>
          {conferenceData?.agenda && conferenceData.agenda.length > 0
          && conferenceData.agenda.map((a: Agenda) => {
            const prelegentsArray = a.prelegents ? [...a.prelegents, a.prelegent_id] : [a.prelegent_id];
            const prelegents = getPrelegents(prelegentsArray);
            return (
              <AgendaItem key={a.title[currentLang]}>
                <AgendaDateContainer>
                  <AgendaTime>
                    <Clock size={18} color="#1C1C1C" />
                    <AgendaTimeText>{a.time_from} - {a.time_to}</AgendaTimeText>
                  </AgendaTime>
                  <AgendaDuraiton>
                    {`(${getMinutesDifference(a.time_from, a.time_to)}min)`}
                  </AgendaDuraiton>
                </AgendaDateContainer>
                <AgendaTextContainer>
                  <OrgText>
                    {a?.title[currentLang]}
                  </OrgText>
                  <OrgDescription>
                    {a?.description[currentLang]}
                  </OrgDescription>
                  <PrelegentsContainer>
                    {prelegents && prelegents.map((p: Prelegent) => (
                      <PrelegentContainer key={p.id}>
                        <PrelegentImage src={p.photo || ''} />
                        <PrelegentTextContainer>
                          <PrelegentName>{p.name}</PrelegentName>
                          <PrelegentRole>{p.title}</PrelegentRole>
                        </PrelegentTextContainer>
                      </PrelegentContainer>
                    ))}
                  </PrelegentsContainer>
                </AgendaTextContainer>
              </AgendaItem>
            );
          })}
        </ScrollContainer>
      </div>
    </>
  );
};
